import React, { useState } from "react";
import styled from "styled-components";
import { RebelColors, RebelModal, RebelModalHeader, RebelModalBody, RebelModalFooter, RebelAccentButton, RebelSecondaryButton, RebelLabel, RebelInput } from "../../../styles/rebel-experiences";
import { updateDocumentArray } from "../../../utils/firestore-functions";
import emailjs from "@emailjs/browser";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { calculateVillaTotalPerPerson } from "./rebel-budget-property-tab";

const EMAILJS_SERVICE_ID = "service_xiqdcy8";
const EMAILJS_SHARE_TEMPLATE_ID = "template_rr1fjov";
const EMAILJS_PUBLIC_KEY = "user_8Qt3ZJv9dlnGhiJefkhBh";

/** Capitalize each word: "john smith" → "John Smith" */
const capitalizeName = (name) =>
    (name || "").trim().replace(/\b\w/g, c => c.toUpperCase());

const displayValue = (val) => {
    if (val === undefined || val === null || isNaN(val)) return "0.00";
    return val.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const RebelBudgetShareModal = ({ isOpen, toggle, config, properties, villaData, categories, tripLength, exchangeRate = 1, budgetId, savedEmail, savedContactName, budgetDB }) => {
    const [nameInput, setNameInput] = useState("");
    const [emailInput, setEmailInput] = useState("");
    const [recipients, setRecipients] = useState([]); // [{name, email}]
    const [isSending, setIsSending] = useState(false);
    const [sentSuccess, setSentSuccess] = useState(false);
    const [sentCount, setSentCount] = useState(0);

    const viewLink = `${window.location.origin}/r/v/${budgetId}`;

    const addRecipient = () => {
        const trimmedEmail = emailInput.trim();
        const trimmedName = nameInput.trim();
        if (trimmedEmail && trimmedEmail.includes("@") && !recipients.some(r => r.email === trimmedEmail)) {
            setRecipients(prev => [...prev, { name: trimmedName || trimmedEmail.split("@")[0], email: trimmedEmail }]);
            setEmailInput("");
            setNameInput("");
        }
    };

    const removeRecipient = (email) => {
        setRecipients(prev => prev.filter(r => r.email !== email));
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            addRecipient();
        }
    };

    const handleSend = async () => {
        if (recipients.length === 0) return;

        setIsSending(true);

        const generatedDate = new Date().toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        });

        // Build property details HTML for the email
        const currencySymbol = (config.currency || "USD$").slice(3);
        const currencyCode = (config.currency || "USD$").slice(0, 3);
        const attendees = config.attendees || 1;
        const propertyLink = `${window.location.origin}/r/v/${budgetId}`;

        const propertyDetailsHtml = properties.map((villaId, i) => {
            const villa = villaData?.[villaId];
            if (!villa) return '';
            const name = villa.villaName || villa.name || `Property ${i + 1}`;
            const location = villa.location || '';
            const perPersonUSD = calculateVillaTotalPerPerson(villa, categories, tripLength, config);
            const perPerson = perPersonUSD * (exchangeRate || 1);
            const totalEstimate = perPerson * attendees;
            const villaHref = villa.villaURL
                ? (villa.villaURL.startsWith('http') ? villa.villaURL : `https://${villa.villaURL}`)
                : propertyLink;

            return `<tr>
                <td style="padding: 10px 16px; border-bottom: 1px solid rgba(253, 181, 6, 0.1);">
                    <a href="${villaHref}" style="color: #fdb506; font-size: 14px; font-weight: 600; text-decoration: none;">${name}</a>
                    ${location ? `<div style="color: #9ca3af; font-size: 11px; margin-top: 2px;">${location}</div>` : ''}
                </td>
                <td style="padding: 10px 16px; border-bottom: 1px solid rgba(253, 181, 6, 0.1); text-align: right;">
                    <div style="color: #ffffff; font-size: 13px; font-weight: 600;">${currencySymbol}${displayValue(totalEstimate)} ${currencyCode}</div>
                    <div style="color: #9ca3af; font-size: 11px; margin-top: 2px;">${currencySymbol}${displayValue(perPerson)} /pp</div>
                </td>
            </tr>`;
        }).join('');

        const propertyDetails = propertyDetailsHtml ? `<table role="presentation" cellpadding="0" cellspacing="0" width="100%" style="background: rgba(253, 181, 6, 0.06); border: 1px solid rgba(253, 181, 6, 0.2); border-radius: 8px; overflow: hidden;">
            <tr><td style="padding: 10px 16px; border-bottom: 1px solid rgba(253, 181, 6, 0.15);"><span style="color: #9ca3af; font-size: 11px; text-transform: uppercase; letter-spacing: 1px;">Property</span></td><td style="padding: 10px 16px; border-bottom: 1px solid rgba(253, 181, 6, 0.15); text-align: right;"><span style="color: #9ca3af; font-size: 11px; text-transform: uppercase; letter-spacing: 1px;">Estimate</span></td></tr>
            ${propertyDetailsHtml}
        </table>` : '';

        let successCount = 0;

        for (const recipient of recipients) {
            try {
                await emailjs.send(EMAILJS_SERVICE_ID, EMAILJS_SHARE_TEMPLATE_ID, {
                    to_email: recipient.email,
                    to_name: capitalizeName(recipient.name),
                    contact_name: capitalizeName(recipient.name),
                    sender_email: savedEmail || "A team member",
                    sender_name: capitalizeName(savedContactName) || "Your colleague",
                    company_name: config.companyName || "Your Team",
                    start_date: config.startDate || "TBD",
                    end_date: config.endDate || "TBD",
                    attendees: config.attendees,
                    num_properties: properties.length,
                    review_link: viewLink,
                    property_details: propertyDetails,
                    generated_date: generatedDate,
                }, EMAILJS_PUBLIC_KEY);
                successCount++;
            } catch (error) {
                console.warn(`Failed to send share email to ${recipient.email}:`, error);
            }
        }

        // Save shared recipients to Firestore so admins can see who received the link
        if (successCount > 0 && budgetId && budgetDB) {
            try {
                for (const r of recipients) {
                    updateDocumentArray(budgetDB, "rebelClientBudgets", budgetId, "sharedRecipients",
                        { name: r.name, email: r.email, sharedAt: new Date().toISOString() }
                    );
                }
            } catch (err) {
                console.warn("Failed to save shared recipients:", err);
            }
        }

        setSentCount(successCount);
        setSentSuccess(true);
        setIsSending(false);
    };

    const handleClose = () => {
        setSentSuccess(false);
        setSentCount(0);
        setRecipients([]);
        setEmailInput("");
        setNameInput("");
        toggle();
    };

    if (sentSuccess) {
        return (
            <RebelModal isOpen={isOpen} toggle={handleClose}>
                <RebelModalHeader>Link Shared</RebelModalHeader>
                <RebelModalBody>
                    <SuccessMessage>
                        <FontAwesomeIcon icon={['fas', 'check-circle']} style={{ marginRight: 8 }} />
                        Shared with {sentCount} team member{sentCount !== 1 ? 's' : ''} successfully!
                    </SuccessMessage>
                    <LinkContainer>
                        <RebelLabel>View-only link shared:</RebelLabel>
                        <LinkDisplay>{viewLink}</LinkDisplay>
                        <CopyButton onClick={() => navigator.clipboard.writeText(viewLink)}>
                            <FontAwesomeIcon icon={['fas', 'copy']} style={{ marginRight: 6 }} />
                            Copy Link
                        </CopyButton>
                    </LinkContainer>
                    <InfoNote>
                        Recipients can view property details and submit their rankings, but cannot edit the comparison.
                    </InfoNote>
                </RebelModalBody>
                <RebelModalFooter>
                    <RebelAccentButton onClick={handleClose}>Done</RebelAccentButton>
                </RebelModalFooter>
            </RebelModal>
        );
    }

    return (
        <RebelModal isOpen={isOpen} toggle={handleClose}>
            <RebelModalHeader>Share with Team</RebelModalHeader>
            <RebelModalBody>
                {/* Updated description text (#9) */}
                <DescriptionText>
                    Send a view-only link to your team so they can review the selected properties and submit their rankings. This shared link is separate from your editable version and does not allow changes to budgets or retreat details. All edits remain in your control, and any updates you make will automatically reflect for everyone.
                </DescriptionText>

                {/* Name + Email input row (#7) */}
                <InputSection>
                    <InputRow>
                        <NameInputWrapper>
                            <RebelLabel>Name</RebelLabel>
                            <RebelInput
                                type="text"
                                value={nameInput}
                                onChange={e => setNameInput(e.target.value)}
                                placeholder="Team member name"
                            />
                        </NameInputWrapper>
                        <EmailInputWrapper>
                            <RebelLabel>Email</RebelLabel>
                            <RebelInput
                                type="email"
                                value={emailInput}
                                onChange={e => setEmailInput(e.target.value)}
                                onKeyDown={handleKeyDown}
                                placeholder="colleague@company.com"
                            />
                        </EmailInputWrapper>
                        <AddButtonWrapper>
                            <AddButton onClick={addRecipient} disabled={!emailInput.trim() || !emailInput.includes("@")}>
                                <FontAwesomeIcon icon={['fas', 'plus']} />
                            </AddButton>
                        </AddButtonWrapper>
                    </InputRow>
                </InputSection>

                {recipients.length > 0 && (
                    <RecipientChipsContainer>
                        {recipients.map(recipient => (
                            <RecipientChip key={recipient.email}>
                                <RecipientInfo>
                                    <RecipientName>{recipient.name}</RecipientName>
                                    <RecipientEmail>{recipient.email}</RecipientEmail>
                                </RecipientInfo>
                                <RemoveChip onClick={() => removeRecipient(recipient.email)}>
                                    <FontAwesomeIcon icon={['fas', 'times']} />
                                </RemoveChip>
                            </RecipientChip>
                        ))}
                    </RecipientChipsContainer>
                )}

                {recipients.length === 0 && (
                    <EmptyHint>Add team members above to share the comparison link.</EmptyHint>
                )}
            </RebelModalBody>
            <RebelModalFooter>
                <RebelSecondaryButton onClick={handleClose}>Cancel</RebelSecondaryButton>
                {/* Changed button text from "Send Invites" to "Share Link" (#10) */}
                <RebelAccentButton onClick={handleSend} disabled={recipients.length === 0 || isSending}>
                    {isSending ? 'Sharing...' : `Share Link${recipients.length > 0 ? ` (${recipients.length})` : ''}`}
                </RebelAccentButton>
            </RebelModalFooter>
        </RebelModal>
    );
};

export default RebelBudgetShareModal;

const SuccessMessage = styled.p`
    color: #10b981;
    font-size: 1.1rem;
    font-weight: 600;
    text-align: center;
    margin-bottom: 1.5rem;
    display: flex;
    align-items: center;
    justify-content: center;
`;

const LinkContainer = styled.div`
    margin-bottom: 1rem;
`;

const LinkDisplay = styled.div`
    background-color: ${RebelColors.gray700};
    border: 1px solid ${RebelColors.gold};
    border-radius: 6px;
    padding: 10px 12px;
    color: ${RebelColors.gold};
    font-size: 0.85rem;
    word-break: break-all;
    margin-bottom: 0.5rem;
`;

const CopyButton = styled.button`
    background: transparent;
    border: 1px solid ${RebelColors.gold};
    color: ${RebelColors.gold};
    border-radius: 4px;
    padding: 4px 12px;
    font-size: 0.8rem;
    cursor: pointer;
    transition: all 0.2s ease;
    display: inline-flex;
    align-items: center;

    &:hover {
        background: ${RebelColors.gold};
        color: #000;
    }
`;

const InfoNote = styled.p`
    color: ${RebelColors.textMuted};
    font-size: 0.8rem;
    text-align: center;
    font-style: italic;
    margin-top: 1rem;
    margin-bottom: 0;
`;

const DescriptionText = styled.p`
    color: ${RebelColors.textMuted};
    margin-bottom: 1.25rem;
    font-size: 0.9rem;
    line-height: 1.5;
`;

const InputSection = styled.div`
    margin-bottom: 12px;
`;

const InputRow = styled.div`
    display: flex;
    gap: 8px;
    align-items: flex-end;
`;

const NameInputWrapper = styled.div`
    flex: 0 0 35%;
`;

const EmailInputWrapper = styled.div`
    flex: 1;
`;

const AddButtonWrapper = styled.div`
    display: flex;
    align-items: flex-end;
`;

const AddButton = styled.button`
    background: ${props => props.disabled ? RebelColors.gray600 : RebelColors.gold};
    color: ${props => props.disabled ? RebelColors.textMuted : '#000'};
    border: 1px solid transparent;
    border-radius: 0.5rem;
    width: 38px;
    min-height: 38px;
    padding: 0.5rem;
    font-weight: 600;
    font-size: 0.875rem;
    cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
    transition: all 0.2s ease;
    opacity: ${props => props.disabled ? 0.5 : 1};
    display: flex;
    align-items: center;
    justify-content: center;
    box-sizing: border-box;

    &:hover:not(:disabled) {
        opacity: 0.9;
    }
`;

const RecipientChipsContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: 6px;
    margin-top: 12px;
`;

const RecipientChip = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    background: rgba(253, 181, 6, 0.08);
    border: 1px solid rgba(253, 181, 6, 0.2);
    border-radius: 8px;
    padding: 8px 12px;
`;

const RecipientInfo = styled.div`
    display: flex;
    gap: 12px;
    align-items: center;
`;

const RecipientName = styled.span`
    color: ${RebelColors.text};
    font-size: 0.85rem;
    font-weight: 500;
`;

const RecipientEmail = styled.span`
    color: ${RebelColors.textMuted};
    font-size: 0.8rem;
`;

const RemoveChip = styled.button`
    background: none;
    border: none;
    color: ${RebelColors.textMuted};
    cursor: pointer;
    padding: 4px 6px;
    font-size: 0.75rem;
    display: flex;
    align-items: center;
    transition: color 0.2s ease;

    &:hover {
        color: #ef4444;
    }
`;

const EmptyHint = styled.p`
    color: ${RebelColors.textMuted};
    font-size: 0.85rem;
    font-style: italic;
    text-align: center;
    margin: 16px 0 0 0;
`;
